import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

/**
 * Carte affichant une facture en retard avec ses détails et actions
 */
const OverdueInvoiceCard = ({
  invoice,
  formatCurrency,
  formatDate,
  onSendReminder,
  onViewDetails,
  onViewHistory
}) => {
  const getSeverityConfig = (severity) => {
    const configs = {
      warning: {
        label: 'Attention',
        bgClass: 'bg-warning/10',
        borderClass: 'border-warning/30',
        textClass: 'text-warning',
        icon: 'AlertCircle',
        badgeClass: 'bg-warning/20 text-warning'
      },
      urgent: {
        label: 'Urgent',
        bgClass: 'bg-orange-500/10',
        borderClass: 'border-orange-500/30',
        textClass: 'text-orange-500',
        icon: 'AlertTriangle',
        badgeClass: 'bg-orange-500/20 text-orange-500'
      },
      critical: {
        label: 'Critique',
        bgClass: 'bg-destructive/10',
        borderClass: 'border-destructive/30',
        textClass: 'text-destructive',
        icon: 'XOctagon',
        badgeClass: 'bg-destructive/20 text-destructive'
      }
    };
    return configs[severity] || configs.warning;
  };

  const severityConfig = getSeverityConfig(invoice.severity);

  const isPaymentPhase = invoice.itemType === 'payment_phase';

  // Build the reminder message for tooltip
  const getReminderMessage = () => {
    const reminderNumber = invoice.daysOverdue > 1 ? ` ${invoice.daysOverdue}` : '';
    return `Rappel${reminderNumber} : Votre paiement de ${formatCurrency(invoice.balance_due, invoice.currencyCode)} pour la facture ${invoice.invoice_number} est dû le ${formatDate(invoice.due_date)}.`;
  };

  return (
    <div
      className={`bg-card border-2 rounded-lg overflow-hidden hover:shadow-lg transition-all ${severityConfig.borderClass}`}
    >
      {/* Header with severity indicator */}
      <div className={`p-4 border-b border-border ${severityConfig.bgClass}`}>
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            {/* Severity Icon */}
            <div className={`p-2 rounded-lg ${severityConfig.badgeClass} flex-shrink-0`}>
              <Icon name={severityConfig.icon} size={24} className={severityConfig.textClass} />
            </div>

            {/* Invoice Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-bold text-foreground">
                  {invoice.invoice_number}
                </h3>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${severityConfig.badgeClass}`}
                >
                  {severityConfig.label}
                </span>
                {isPaymentPhase && (
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-primary/20 text-primary">
                    Phase {invoice.phase_number}
                  </span>
                )}
              </div>

              {/* Payment Phase Description */}
              {isPaymentPhase && invoice.phase_description && (
                <div className="mb-2">
                  <p className="text-sm text-muted-foreground italic">
                    {invoice.phase_description}
                  </p>
                </div>
              )}

              {/* Milestone Status for Payment Phases */}
              {isPaymentPhase && invoice.milestone_status && (
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Statut du jalon:</span>
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                      invoice.milestone_status === 'completed'
                        ? 'bg-success/10 text-success'
                        : invoice.milestone_status === 'in_progress'
                        ? 'bg-blue-500/10 text-blue-500'
                        : invoice.milestone_status === 'delayed'
                        ? 'bg-warning/10 text-warning'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {invoice.milestone_status === 'completed'
                      ? 'Complété'
                      : invoice.milestone_status === 'in_progress'
                      ? 'En cours'
                      : invoice.milestone_status === 'delayed'
                      ? 'Retardé'
                      : 'Non démarré'}
                  </span>
                  {invoice.milestone_completion_percentage > 0 && (
                    <span className="text-xs text-muted-foreground">
                      ({invoice.milestone_completion_percentage}% complété)
                    </span>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                {/* Dentist */}
                <div>
                  <p className="text-muted-foreground text-xs mb-0.5">Dentiste</p>
                  <p className="font-semibold text-foreground">{invoice.dentistName}</p>
                  <p className="text-xs text-muted-foreground">{invoice.dentistEmail}</p>
                </div>

                {/* Case/Patient */}
                {invoice.caseNumber && (
                  <div>
                    <p className="text-muted-foreground text-xs mb-0.5">Cas / Patient</p>
                    <p className="font-semibold text-foreground">{invoice.caseNumber}</p>
                    {invoice.patientName && (
                      <p className="text-xs text-muted-foreground">{invoice.patientName}</p>
                    )}
                  </div>
                )}

                {/* Country */}
                <div>
                  <p className="text-muted-foreground text-xs mb-0.5">Pays</p>
                  <p className="font-semibold text-foreground flex items-center gap-2">
                    <Icon name="Globe" size={14} className="text-muted-foreground" />
                    {invoice.countryName}
                  </p>
                  <p className="text-xs text-muted-foreground">Devise: {invoice.currencyCode}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Days Overdue Badge */}
          <div className="text-right flex-shrink-0 ml-4">
            <div className={`p-3 rounded-lg ${severityConfig.badgeClass}`}>
              <p className={`text-3xl font-bold ${severityConfig.textClass}`}>
                {invoice.daysOverdue}
              </p>
              <p className="text-xs font-medium text-muted-foreground">
                jour{invoice.daysOverdue > 1 ? 's' : ''} de retard
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Body - Financial Details */}
      <div className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
          {/* Issue Date */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Date d'émission</p>
            <p className="text-sm font-medium text-foreground flex items-center gap-1">
              <Icon name="Calendar" size={14} className="text-muted-foreground" />
              {formatDate(invoice.issue_date)}
            </p>
          </div>

          {/* Due Date */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Date d'échéance</p>
            <p className={`text-sm font-bold flex items-center gap-1 ${severityConfig.textClass}`}>
              <Icon name="CalendarX" size={14} />
              {formatDate(invoice.due_date)}
            </p>
          </div>

          {/* Total Amount */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Montant total</p>
            <p className="text-sm font-bold text-foreground">
              {formatCurrency(invoice.total_amount, invoice.currencyCode)}
            </p>
            {invoice.discount_amount > 0 && (
              <p className="text-xs text-success flex items-center gap-1">
                <Icon name="Tag" size={10} />
                Remise: -{formatCurrency(invoice.discount_amount, invoice.currencyCode)}
              </p>
            )}
          </div>

          {/* Amount Paid */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Montant payé</p>
            <p className="text-sm font-semibold text-success">
              {formatCurrency(invoice.amount_paid, invoice.currencyCode)}
            </p>
          </div>

          {/* Balance Due */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Solde dû</p>
            <p className={`text-lg font-bold ${severityConfig.textClass}`}>
              {formatCurrency(invoice.balance_due, invoice.currencyCode)}
            </p>
          </div>
        </div>

        {/* Notes if available */}
        {invoice.notes && (
          <div className="bg-muted/30 border border-border rounded-lg p-3 mb-4">
            <p className="text-xs font-semibold text-foreground mb-1 flex items-center gap-1">
              <Icon name="FileText" size={12} />
              Notes
            </p>
            <p className="text-sm text-muted-foreground">{invoice.notes}</p>
          </div>
        )}

        {/* Reminder Message Preview */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 mb-4">
          <p className="text-xs font-semibold text-primary mb-1 flex items-center gap-1">
            <Icon name="MessageSquare" size={12} />
            Message du rappel qui sera envoyé
          </p>
          <p className="text-sm text-foreground italic">
            "{getReminderMessage()}"
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-border">
          {/* Send Reminder Button - PRIMARY ACTION */}
          <Button
            variant="default"
            size="sm"
            onClick={() => onSendReminder(invoice)}
            iconName="Send"
            iconPosition="left"
            className="bg-primary hover:bg-primary/90 font-semibold"
            title={getReminderMessage()}
          >
            Envoyer un rappel de paiement
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={onViewDetails}
            iconName="Eye"
            iconPosition="left"
          >
            {isPaymentPhase ? 'Voir la facture parente' : 'Voir la facture'}
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={onViewHistory}
            iconName="History"
            iconPosition="left"
          >
            Alerte retards
          </Button>

          {invoice.dentistEmail && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const subject = isPaymentPhase
                  ? `Rappel de paiement - ${invoice.parentInvoiceNumber} - ${invoice.phase_name} (Phase ${invoice.phase_number})`
                  : `Rappel de paiement - Facture ${invoice.invoice_number}`;
                window.open(`mailto:${invoice.dentistEmail}?subject=${encodeURIComponent(subject)}`, '_blank');
              }}
              iconName="Mail"
              iconPosition="left"
            >
              Envoyer un email
            </Button>
          )}

          {/* Status indicator */}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Statut:</span>
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                invoice.status === 'sent'
                  ? 'bg-blue-500/10 text-blue-500'
                  : invoice.status === 'pending'
                  ? 'bg-warning/10 text-warning'
                  : invoice.status === 'partially_paid'
                  ? 'bg-orange-500/10 text-orange-500'
                  : 'bg-primary/10 text-primary'
              }`}
            >
              {invoice.status === 'sent'
                ? 'Envoyée'
                : invoice.status === 'pending'
                ? 'En attente'
                : invoice.status === 'partially_paid'
                ? 'Partiellement payée'
                : invoice.status}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverdueInvoiceCard;